import { CKEditorComponent } from '../../../components/ckeditor';

// #region Type aliases (6)

export interface CitationModel {
  references: NumberedCitation[];
  unnumbered_references: UnnumberedCitation[];
  deletedUnnumberedCitationId?: string;
  deletedNumberedCitationId?: string;
  selectedNumberedCitationId?: string;
  copiedNumberedCitationId?: string;
  delFromDocCitationId?: string;
  delFromDocCitationCount?: string;
  fragmentLinks?: FragmentLinks;
}
export type NumberedCitation = ICitation;
export type ReferencePrefixType = 'number' | 'roman' | 'alphabet' | '';
export type SuggestionType = 'format' | 'replace' | 'deletion' | 'insertion';
export type UnnumberedCitation = Omit<ICitation, 'referencedBy'>;
export type UnnumberedReferencePrefixType = 'bullet' | 'circle' | 'dash' | '';

// #endregion Type aliases (6)

// #region Interfaces (16)

export interface ChevronData {
  // #region Properties (3)

  id: string;
  navDirection: string;
  referencedBy: string[];

  // #endregion Properties (3)
}

export interface ContextMenuCoords {
  // #region Properties (6)

  clientX: number;
  left: number;
  marginLeft: string;
  marginRight: string;
  offsetHeight: number;
  top: number;

  // #endregion Properties (6)
}

export interface FragmentLinks {
  // #region Properties (2)
  incomingLinks: FragmentLink[];
  outgoingLinks: FragmentLink[];
  // #endregion Properties (2)
}

export interface FragmentLink {
  // #region Properties (4)
  source: string;
  target: string;
  isExternal: boolean;
  isValid: boolean;
  // #endregion Properties (4)
}

export interface ICitation {
  // #region Properties (5)

  citationId: Required<string>;
  citationString: Required<string>;
  editable: boolean;
  isCitationDeleted?: boolean;
  evidenceLevel?: string;
  referencedBy: Required<string[]>;

  // #endregion Properties (5)
}

export interface ReferenceSource {
  list: () => any[];
  setList: (refs: any[]) => void;
  dispatchUpdate: (refs: any[]) => void;
  onSelect?: (citationId: string) => void;
}

export interface IComment {
  // #region Properties (5)

  attributes: {
    status: CommentStatus;
    timestamp?: Date;
    username?: string;
    commentType: string;
  };
  authorId: string;
  commentId?: string;
  content: string;
  createdAt?: Date;

  // #endregion Properties (5)
}

export interface ICommentThread {
  // #region Properties (8)

  archivedAt?: unknown;
  attributes?: unknown;
  comments: IComment[];
  context?: { type: string; value: string };
  resolvedAt?: Date;
  resolvedBy?: string;
  threadId: string;
  unlinkedAt?: unknown;
  isRHPComment?: boolean;
  id?: string;
  remove?: () => {};

  // #endregion Properties (8)
}

export interface IDecorationCitation extends CitationModel {
  // #region Properties (10)

  chevronData?: ChevronData;
  counterCase?: string;
  isCitationUpdated?: boolean;
  orderedReferenceIds?: string[];
  prefix?: string;
  referencePrefix?: ReferencePrefixType;
  selectedReferenceId?: string;
  selectedUnnumberedCitationForDelete?: string;
  sufix?: string;
  unnumberReferencePrefix?: UnnumberedReferencePrefixType;
  deletedCitationFromRHP?: string;
  createdReferenceId?: string;
  isTrackChangesEnabled?: boolean;

  // #endregion Properties (10)
}

export interface IEntry {
  // #region Properties (22)

  aspectNames?: string[];
  avatarId?: string;
  capabilities?: {
    isAdmin?: boolean;
    isGuest?: boolean;
    isMutable?: boolean;
  };
  company?: {
    organization?: string;
    address1?: string;
    address2?: string;
    address3?: string;
    postcode?: string;
    telephone?: string;
    fax?: string;
    email?: string;
  };
  description?: string;
  displayName?: string;
  email: Required<string>;
  emailNotificationsEnabled?: boolean;
  enabled: Required<boolean>;
  firstName: Required<string>;
  googleId?: string;
  id: Required<string>;
  instantMessageId?: string;
  jobTitle?: string;
  lastName?: string;
  location?: string;
  mobile?: string;
  properties?: {};
  skypeId?: string;
  statusUpdatedAt?: Date | string;
  telephone?: string;
  userStatus?: string;

  // #endregion Properties (22)
}

export interface IPanel {
  // #region Properties (2)

  citations?: IDecorationCitation;
  documentContent: string;

  // #endregion Properties (2)
}

export interface IPerson {
  // #region Properties (1)

  entry: IEntry;

  // #endregion Properties (1)
}

export interface ISuggestion {
  // #region Properties (7)

  attributes: {
    uniqueId: string;
  };
  authorId: string;
  createdAt: string;
  data:
    | object
    | {
        key?: string;
        oldValue?: unknown;
        newValue?: unknown;
        commandName?: string;
        commandParams?: unknown[];
      };
  hasComments: boolean;
  id: string;
  type: SuggestionType;
  isRHPSuggestion?: boolean;

  // #endregion Properties (7)
}

export interface ITab {
  // #region Properties (5)

  count?: number;
  disabled?: boolean;
  heading?: string;
  label: string;
  selector?: any;

  // #endregion Properties (5)
}

export interface ITrackChange {
  // #region Properties (2)

  commentThreads: ICommentThread[];
  suggestions: ISuggestion[];

  // #endregion Properties (2)
}

export interface IUserInfo {
  // #region Properties (3)

  avatar?: string;
  id: string;
  name: string;

  // #endregion Properties (3)
}

export interface LoadingState {
  // #region Properties (3)

  completed: boolean;
  failed: boolean;
  processing: boolean;

  // #endregion Properties (3)
}

export interface NumberedRefToDeleteFromDoc extends NumberedCitation {
  // #region Properties (1)

  citationCount: string;

  // #endregion Properties (1)
}

export interface ReferenceEditor {
  // #region Properties (2)

  isReferenceEditorClicked: boolean;
  referenceEditorInstance: CKEditorComponent;

  // #endregion Properties (2)
}

export interface IDecoratorsCounters {
  referencesCount: number;
  unnumberedReferencesCount: number;
  keywordCount: number;
}

// #endregion Interfaces (16)

// #region Enums (4)

export enum ActionTypes {
  COPY = 'Copy citations',
  DELETE_NUMBERED = 'Delete citations',
  DELETE_UNNUMBERED = 'Delete unnumbered reference',
  PREVIOUS_NUMBERED = 'Jump to previous citation',
  NEXT_NUMBERED = 'Jump to next citation',
  CONVERT_TO_NUMBERED = 'ConvertToNumbered',
}

export enum CommentStatus {
  APPROVE = 'approve',
  REJECT = 'reject',
  RESOLVE = 'resolve',
  DRAFT = 'draft',
}

export enum DIRECTION {
  RIGHT = 'right',
  LEFT = 'left',
}

export enum TabIndex {
  referencesCount,
  unnumberedReferencesCount,
  keywordCount,
}

// #endregion Enums (4)
